import React from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp, FileText, Briefcase, CreditCard, AlertTriangle,
  ArrowRight, Target, Euro, Clock, Users, CheckCircle, XCircle,
  Building2, ChevronRight
} from 'lucide-react';
import {
  getKPIs, ingenieurs, getProductionIngenieur,
  getFilteredAffaires, getFilteredDevis, getFilteredFactures,
  getClientById, getIngenieurById, getSocieteById, getAgenceById,
  statutDevisLabels, statutFactureLabels, societes
} from '../data/mockData';
import { useFilters } from '../App';

const Dashboard = () => {
  const filters = useFilters();
  const kpis = getKPIs(filters);

  const filteredFactures = getFilteredFactures(filters);
  const filteredDevis = getFilteredDevis(filters);
  const filteredAffaires = getFilteredAffaires(filters);

  const facturesEnRetard = filteredFactures.filter(f => f.statut === 'en_retard');
  const devisEnvoyes = filteredDevis.filter(d => d.statut === 'envoye');
  const affairesEnCours = filteredAffaires.filter(a => a.statut === 'en_cours');

  // Filtrer les ingénieurs selon l'agence sélectionnée
  const filteredIngenieurs = filters.agenceId
    ? ingenieurs.filter(i => i.agenceId === filters.agenceId)
    : ingenieurs;

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(montant);
  };

  const activeSociete = filters.societeId ? getSocieteById(filters.societeId) : null;
  const activeAgence = filters.agenceId ? getAgenceById(filters.agenceId) : null;

  return (
    <div className="page-container dashboard">
      <div className="page-header">
        <div>
          <h1><TrendingUp size={28} /> Tableau de bord</h1>
          <div className="page-meta">
            <span><Building2 size={16} /> {activeSociete ? activeSociete.nom : 'Toutes sociétés'}</span>
            {activeAgence && <span>Agence {activeAgence.nom}</span>}
            <span><Clock size={16} /> Année 2025</span>
          </div>
        </div>
      </div>

      {/* KPIs principaux */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="stat-card stat-primary">
          <div className="stat-icon"><Euro size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">Production totale</p>
            <p className="stat-value">{formatMontant(kpis.productionTotale)}</p>
          </div>
        </div>
        <div className="stat-card stat-success">
          <div className="stat-icon"><Target size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">Objectif CA</p>
            <p className="stat-value">{kpis.pourcentageObjectif}%</p>
          </div>
        </div>
        <div className="stat-card stat-warning">
          <div className="stat-icon"><FileText size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">Devis en attente</p>
            <p className="stat-value">{formatMontant(kpis.devisEnAttente)}</p>
          </div>
        </div>
        <div className="stat-card stat-info">
          <div className="stat-icon"><CheckCircle size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">Devis validés</p>
            <p className="stat-value">{formatMontant(kpis.devisValides)}</p>
          </div>
        </div>
      </div>

      {/* Barre de progression objectif */}
      <div className="dashboard-card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h3 style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 600 }}>
            <Target size={18} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
            Avancement vers l'objectif de chiffre d'affaires
            {activeSociete && <span style={{ fontWeight: 400, color: 'var(--gray-500)', marginLeft: '0.5rem' }}>({activeSociete.nom})</span>}
          </h3>
          <span style={{ fontWeight: 600, color: 'var(--primary)' }}>
            {formatMontant(kpis.productionTotale)} / {formatMontant(kpis.objectifCA)}
          </span>
        </div>
        <div className="progress-bar" style={{ height: '12px' }}>
          <div className="progress-fill" style={{ width: `${Math.min(kpis.pourcentageObjectif, 100)}%`, background: 'linear-gradient(90deg, #2563EB, #60A5FA)' }}></div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', fontSize: '0.8125rem', color: 'var(--gray-500)' }}>
          <span>0%</span>
          <span>{kpis.pourcentageObjectif}% atteint</span>
          <span>100%</span>
        </div>
      </div>

      {/* KPIs secondaires */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: '2rem' }}>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#D1FAE5', color: '#059669' }}><CreditCard size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">Montant facturé</p>
            <p className="stat-value" style={{ fontSize: '1.5rem' }}>{formatMontant(kpis.montantFacture)}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#FEF3C7', color: '#D97706' }}><Clock size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">Montant à facturer</p>
            <p className="stat-value" style={{ fontSize: '1.5rem' }}>{formatMontant(kpis.montantAFacturer)}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#EDE9FE', color: '#7C3AED' }}><Users size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">Sous-traité</p>
            <p className="stat-value" style={{ fontSize: '1.5rem' }}>{formatMontant(kpis.montantSousTraite)}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#DBEAFE', color: '#2563EB' }}><Briefcase size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">Affaires en cours</p>
            <p className="stat-value" style={{ fontSize: '1.5rem' }}>{affairesEnCours.length}</p>
          </div>
        </div>
      </div>

      {/* Répartition par société (seulement si toutes sociétés) */}
      {!filters.societeId && (
        <div className="dashboard-card" style={{ padding: '1.25rem', marginBottom: '1.5rem' }}>
          <div className="card-header">
            <h3><Building2 size={20} /> Répartition par société</h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${societes.length}, 1fr)`, gap: '1rem' }}>
            {societes.map(s => {
              const socKPIs = getKPIs({ societeId: s.id, agenceId: filters.agenceId });
              const socAffaires = getFilteredAffaires({ societeId: s.id, agenceId: filters.agenceId });
              return (
                <div key={s.id} style={{
                  padding: '1rem', background: 'var(--gray-50)', borderRadius: 'var(--radius)',
                  borderLeft: '4px solid var(--primary)'
                }}>
                  <h4 style={{ margin: '0 0 0.75rem', fontSize: '1rem', fontWeight: 700 }}>{s.nom}</h4>
                  <p style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', margin: '0 0 0.25rem' }}>{s.type}</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginTop: '0.75rem' }}>
                    <div>
                      <p style={{ fontSize: '0.6875rem', color: 'var(--gray-400)', margin: 0, textTransform: 'uppercase' }}>Production</p>
                      <p style={{ margin: 0, fontWeight: 700, color: 'var(--primary)' }}>{formatMontant(socKPIs.productionTotale)}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '0.6875rem', color: 'var(--gray-400)', margin: 0, textTransform: 'uppercase' }}>Affaires</p>
                      <p style={{ margin: 0, fontWeight: 700 }}>{socAffaires.filter(a => a.statut === 'en_cours').length} en cours</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '0.6875rem', color: 'var(--gray-400)', margin: 0, textTransform: 'uppercase' }}>Objectif</p>
                      <p style={{ margin: 0, fontWeight: 600 }}>{socKPIs.pourcentageObjectif}%</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '0.6875rem', color: 'var(--gray-400)', margin: 0, textTransform: 'uppercase' }}>Facturé</p>
                      <p style={{ margin: 0, fontWeight: 600, color: '#059669' }}>{formatMontant(socKPIs.montantFacture)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="dashboard-row">
        {/* Production par ingénieur */}
        <div className="dashboard-card" style={{ padding: '1.25rem' }}>
          <div className="card-header">
            <h3><Users size={20} /> Production par ingénieur</h3>
            <Link to="/affaires" className="btn btn-sm btn-secondary">Voir tout <ArrowRight size={14} /></Link>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {filteredIngenieurs.map(ing => {
              const prod = getProductionIngenieur(ing.id, filters);
              return (
                <div key={ing.id} style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '0.75rem', background: 'var(--gray-50)', borderRadius: 'var(--radius)'
                }}>
                  <div className="user-avatar user-avatar-sm" style={{ background: ing.couleur, flexShrink: 0 }}>
                    {ing.initiales}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                      <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{ing.prenom} {ing.nom}</span>
                      <span style={{ fontWeight: 600, color: 'var(--primary)', fontSize: '0.875rem' }}>{formatMontant(prod.total)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                      <span>Production propre : {formatMontant(prod.propre)}</span>
                      <span>{ing.role}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Alertes et actions */}
        <div className="dashboard-card alerts-card">
          <div className="card-header">
            <h3><AlertTriangle size={20} /> Alertes & Actions</h3>
          </div>
          <div className="alerts-list">
            {facturesEnRetard.map(f => {
              const client = getClientById(f.clientId);
              const societe = getSocieteById(f.societeId);
              return (
                <div key={f.id} className="alert-item alert-danger">
                  <XCircle size={18} />
                  <div className="alert-content">
                    <p>Facture {f.numero} en retard</p>
                    <span className="alert-detail">
                      {client?.nom} — {formatMontant(f.montantTTC)} TTC
                      <span className="badge badge-secondary" style={{ marginLeft: '0.5rem', fontSize: '0.625rem' }}>{societe?.nom}</span>
                    </span>
                  </div>
                </div>
              );
            })}
            {devisEnvoyes.map(d => {
              const client = getClientById(d.clientId);
              const societe = getSocieteById(d.societeId);
              return (
                <div key={d.id} className="alert-item alert-warning">
                  <Clock size={18} />
                  <div className="alert-content">
                    <p>Devis {d.numero} en attente</p>
                    <span className="alert-detail">
                      {client?.nom} — {formatMontant(d.montantHT)} HT — Envoyé le {new Date(d.dateEnvoi).toLocaleDateString('fr-FR')}
                      <span className="badge badge-secondary" style={{ marginLeft: '0.5rem', fontSize: '0.625rem' }}>{societe?.nom}</span>
                    </span>
                  </div>
                </div>
              );
            })}
            {facturesEnRetard.length === 0 && devisEnvoyes.length === 0 && (
              <div className="empty-alerts">
                <CheckCircle size={32} />
                <p>Aucune alerte en cours</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dernières affaires */}
      <div className="dashboard-card" style={{ marginTop: '1.5rem' }}>
        <div className="card-header" style={{ padding: '1.25rem', borderBottom: '1px solid var(--gray-200)' }}>
          <h3><Briefcase size={20} /> Affaires récentes</h3>
          <Link to="/affaires" className="btn btn-sm btn-secondary">Toutes les affaires <ArrowRight size={14} /></Link>
        </div>
        <div className="recent-list">
          {filteredAffaires.slice(0, 5).map(a => {
            const client = getClientById(a.clientId);
            const ingenieur = getIngenieurById(a.ingenieurId);
            const societe = getSocieteById(a.societeId);
            return (
              <Link to={`/affaires/${a.id}`} key={a.id} className="recent-item" style={{ textDecoration: 'none', color: 'inherit' }}>
                <div className="recent-avatar" style={{ background: ingenieur?.couleur || '#6B7280' }}>
                  {ingenieur?.initiales}
                </div>
                <div className="recent-info">
                  <strong>{a.numero} — {a.designation}</strong>
                  <span>{client?.nom} • {a.ville} • {a.codeMission}</span>
                </div>
                <span className="badge badge-secondary" style={{ fontSize: '0.625rem' }}>{societe?.nom}</span>
                <span style={{ fontWeight: 600, color: 'var(--primary)' }}>{formatMontant(a.montantHT)}</span>
                <span className={`badge ${a.statut === 'en_cours' ? 'badge-primary' : a.statut === 'terminee' ? 'badge-success' : 'badge-warning'}`}>
                  {a.statut === 'en_cours' ? 'En cours' : a.statut === 'terminee' ? 'Terminée' : 'Suspendue'}
                </span>
                <ChevronRight size={18} style={{ color: 'var(--gray-400)' }} />
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
